import { FC, useState, useEffect } from 'react';
import Link from 'next/link';
import { cn } from 'helpers/utils/classnames';

export interface AmplienceTabNavigationProps {
  items: {
    title: string;
    url: string;
  }[];
}

const AmplienceTabNavigation: FC<AmplienceTabNavigationProps> = ({ items }) => {
  const [activeTab, setActiveTab] = useState<{ title: string; url: string } | undefined>(undefined);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const activeTab = items.find((item) => window.location.pathname.includes(item.url));
      setActiveTab(activeTab);
    }
  }, []);
  return (
    <nav
      data-testid="tab-navigation-component"
      role="navigation"
      aria-label="Tab Navigation"
      className="flex items-center justify-center gap-8"
    >
      {/* Desktop view - tabs */}
      <ul className="m-0 hidden list-none items-center justify-center gap-8 p-0 md:flex" role="tablist">
        {items.map((item) => (
          <li
            key={item.title}
            role="presentation"
            className={cn({ 'border-b-2 border-primary-gold': activeTab?.url === item.url })}
          >
            <Link
              href={item.url || ''}
              key={item.title}
              className={cn('text-base', { 'font-semibold': activeTab?.url === item.url })}
              aria-current={activeTab?.url === item.url ? 'page' : undefined}
              role="tab"
              aria-selected={activeTab?.url === item.url}
              data-testid={`tab-navigation-item-${item.title}`}
              aria-label={item.title}
            >
              {item.title}
            </Link>
          </li>
        ))}
      </ul>

      {/* Mobile view - dropdown */}
      <div className="relative mx-auto w-full md:hidden">
        <button
          className="flex w-full items-center justify-between rounded-md bg-neutral-225 p-3"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
          aria-label={`Current tab: ${activeTab?.title || 'None selected'}`}
        >
          <span className="text-sm">{activeTab?.title}</span>
        </button>

        {isDropdownOpen && (
          <ul className="mt-1 w-full rounded-md" role="listbox">
            {items.map((item) => {
              if (item.url !== activeTab?.url) {
                return (
                  <li key={item.title} className="w-full p-2" role="option" aria-selected={activeTab?.url === item.url}>
                    <Link
                      href={item.url || ''}
                      className="block w-full text-sm"
                      onClick={() => setIsDropdownOpen(false)}
                      data-testid={`tab-navigation-item-mobile-${item.title}`}
                      aria-label={item.title}
                    >
                      {item.title}
                    </Link>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        )}
      </div>
    </nav>
  );
};
export default AmplienceTabNavigation;
